import { useProductPicker } from "@/hooks/menu/useProductPicker";
import ProductPickerSelect from "@/components/menu/ProductPickerSelect";
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/Input";
import { TextArea } from "@/components/Textarea";
import { Label } from "@/components/Label";
import { useUploadMedia } from "@/lib/useUploadMedia";
import {
  convertPriceFromCent,
  convertPriceToCent,
} from "@/lib/utils";
import {
  MenuFormulaComponentItem,
  MenuFormulaComponentItemFormData,
} from "@/redux/services/menu/menu_formula.type";
import {
  useCreateMenuFormulaComponentItemMutation,
  useUpdateMenuFormulaComponentItemMutation,
} from "@/redux/services/menu/menu_formula.api";
import { useAppSelector } from "@/redux/features/hooks";
import toast from "react-hot-toast";
import { logger } from "@/services/Logger";
import {
  identityImagePayload,
  identityPricePayload,
  identityTextPayload,
  inheritedTextForForm,
  refillOnProductChange,
} from "@/lib/catalogueIdentity";
import { toastApiError } from "@/lib/apiErrors";

interface ComponentItemFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  item?: MenuFormulaComponentItem;
  componentId: string;
  onSuccess?: () => void;
}

const ComponentItemFormModal: React.FC<ComponentItemFormModalProps> = ({
  isOpen,
  onClose,
  item,
  componentId,
  onSuccess,
}) => {
  const [mode, setMode] = useState<"product" | "standalone">("product");
  const [selectedProductId, setSelectedProductId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState(0);
  const [priceModifier, setPriceModifier] = useState(0);
  const [sortOrder, setSortOrder] = useState(1);
  const [imagePreview, setImagePreview] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);

  const restaurant = useAppSelector((state) => state.user.currentRestaurant);

  const [createItem, { isLoading: isCreating }] =
    useCreateMenuFormulaComponentItemMutation();
  const [updateItem, { isLoading: isUpdating }] =
    useUpdateMenuFormulaComponentItemMutation();
  const { uploadMedia } = useUploadMedia();

  // Server-side search, 50 dishes a page (SFRESTO-166).
  const picker = useProductPicker({
    restaurantId: restaurant?.id,
    enabled: isOpen,
    selectedId: selectedProductId || item?.product?.id,
  });
  const { findProduct } = picker;

  // Prefill again once the item's dish is known, so inherited values show as
  // blank.
  const productsLoaded = picker.isKnown(item?.product?.id);

  const isEditing = !!item;
  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    if (item) {
      if (item.product) {
        setMode("product");
        setSelectedProductId(item.product.id);
      } else {
        setMode("standalone");
        setSelectedProductId("");
      }
      // The API serializes the values resolved through the Product; show
      // the ones the item merely inherits as blank ("hériter du plat").
      const product = item.product
        ? findProduct(item.product.id)
        : undefined;
      setName(
        product
          ? inheritedTextForForm(item.name, product.title)
          : (item.name ?? "")
      );
      setDescription(
        product
          ? inheritedTextForForm(item.description, product.description ?? null)
          : (item.description ?? "")
      );
      setPrice(
        item.price == null || (product && item.price === product.basePrice)
          ? 0
          : convertPriceFromCent(item.price)
      );
      setPriceModifier(convertPriceFromCent(item.priceModifier));
      setSortOrder(item.sortOrder);
      if (item.image?.url) {
        setImagePreview(item.image.url);
      }
    } else {
      setMode("product");
      setSelectedProductId("");
      setName("");
      setDescription("");
      setPrice(0);
      setPriceModifier(0);
      setSortOrder(1);
      setImagePreview("");
      setImageFile(null);
    }
  }, [item, isOpen, productsLoaded]);

  // Fields still showing the previous dish's values follow the new dish.
  const handleProductChange = (productId: string) => {
    const previous = findProduct(selectedProductId);
    const next = findProduct(productId);
    setSelectedProductId(productId);
    if (!next) return;
    // Blank = inherit from the dish (see the field placeholders).
    setName((v) => refillOnProductChange(v, previous?.title, ""));
    setDescription((v) => refillOnProductChange(v, previous?.description, ""));
    setPrice((v) =>
      previous && v === convertPriceFromCent(previous.basePrice) ? 0 : v
    );
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const validateForm = (): boolean => {
    if (mode === "standalone" && !name.trim()) {
      toast.error("Le nom est requis pour un élément personnalisé");
      return false;
    }
    if (mode === "product" && !selectedProductId) {
      toast.error("Veuillez sélectionner un plat");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      let uploadedIri: string | undefined;
      if (imageFile) {
        const uploadedMedia = await uploadMedia(imageFile);
        if (uploadedMedia) {
          uploadedIri = `/api/media_objects/${uploadedMedia.id}`;
        }
      }
      // Standalone items own their image; keep the one they show.
      const imagePath =
        uploadedIri ??
        (item?.image ? `/api/media_objects/${item.image.id}` : undefined);

      // A Product-linked item follows the Product's name, description, price
      // and image: only real overrides are written on the item (SFRESTO-122).
      const product = findProduct(selectedProductId);
      const sameProduct = !!item && item.product?.id === selectedProductId;
      const priceCents = Math.round(convertPriceToCent(price));
      const identity = {
        name: identityTextPayload(name, {
          productValue: product ? product.title : undefined,
          initialValue: sameProduct ? item?.name : undefined,
        }),
        description: identityTextPayload(description, {
          productValue: product ? (product.description ?? null) : undefined,
          initialValue: sameProduct ? item?.description : undefined,
        }),
        price: identityPricePayload(priceCents, {
          productValue: product ? product.basePrice : undefined,
          initialValue: sameProduct ? item?.price : undefined,
        }),
        image: identityImagePayload({
          uploadedIri,
          currentImageId: item?.image?.id,
          productImageId: product ? (product.image?.id ?? null) : undefined,
        }),
      };

      if (isEditing && item) {
        const updateData: Partial<
          Omit<MenuFormulaComponentItemFormData, "menuFormulaComponent">
        > = {
          priceModifier: convertPriceToCent(priceModifier),
          sortOrder,
        };

        if (mode === "product") {
          updateData.product = `/api/products/${selectedProductId}`;
          for (const key of [
            "name",
            "description",
            "price",
            "image",
          ] as const) {
            if (identity[key] !== undefined) {
              (updateData as Record<string, unknown>)[key] = identity[key];
            }
          }
        } else {
          updateData.product = null;
          updateData.name = name;
          updateData.description = description || null;
          updateData.price = priceCents;
          if (imagePath) updateData.image = imagePath;
        }

        await updateItem({ id: item.id, data: updateData }).unwrap();
        toast.success("Élément mis à jour avec succès");
      } else {
        const data: MenuFormulaComponentItemFormData = {
          menuFormulaComponent: `/api/menu_formula_components/${componentId}`,
          priceModifier: convertPriceToCent(priceModifier),
          sortOrder,
        };

        if (mode === "product") {
          data.product = `/api/products/${selectedProductId}`;
          if (identity.name) data.name = identity.name;
          if (identity.description) data.description = identity.description;
          if (identity.price) data.price = identity.price;
          if (uploadedIri) data.image = uploadedIri;
        } else {
          data.name = name;
          if (description) data.description = description;
          data.price = priceCents;
          if (imagePath) data.image = imagePath;
        }

        await createItem(data).unwrap();
        toast.success("Élément ajouté avec succès");
      }

      onSuccess?.();
      onClose();
    } catch (error: any) {
      logger.error("ComponentItemFormModal", "Error saving item:", error);
      toastApiError(error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEditing ? "Modifier l'élément" : "Ajouter un élément"}
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {/* Mode toggle */}
            <div>
              <Label label="Type d'élément" />
              <div className="flex gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setMode("product")}
                  className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                    mode === "product"
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:border-primary/30"
                  }`}
                >
                  Plat
                </button>
                <button
                  type="button"
                  onClick={() => setMode("standalone")}
                  className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                    mode === "standalone"
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:border-primary/30"
                  }`}
                >
                  Personnalisé
                </button>
              </div>
            </div>

            {/* Dish selector */}
            {mode === "product" && (
              <div>
                <Label label="Plat" htmlFor="product-select" />
                <ProductPickerSelect
                  id="product-select"
                  picker={picker}
                  value={selectedProductId}
                  onChange={handleProductChange}
                  emptyLabel="-- Sélectionner un plat --"
                />
              </div>
            )}

            {/* Name (required for standalone, optional override for menu_item) */}
            <div>
              <Input
                label={
                  mode === "standalone" ? "Nom" : "Nom personnalisé (optionnel)"
                }
                type="text"
                id="item-name"
                placeholder={
                  mode === "standalone"
                    ? "Nom de l'élément"
                    : "Laisser vide pour hériter du plat"
                }
                required={mode === "standalone"}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div>
              <TextArea
                label="Description"
                placeholder="Description de l'élément"
                id="item-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Input
                  label={
                    mode === "standalone"
                      ? "Prix d'affichage"
                      : "Prix (optionnel)"
                  }
                  type="number"
                  id="item-price"
                  placeholder="0"
                  min={0}
                  step="0.01"
                  value={price}
                  onChange={(e) => setPrice(parseFloat(e.target.value) || 0)}
                />
              </div>
              <div>
                <Input
                  label="Supplément"
                  type="number"
                  id="item-priceModifier"
                  placeholder="0"
                  min={0}
                  step="0.01"
                  value={priceModifier}
                  onChange={(e) =>
                    setPriceModifier(parseFloat(e.target.value) || 0)
                  }
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">
              Le supplément est ajouté au prix de base de la formule
            </p>

            <div>
              <Input
                label="Position"
                type="number"
                id="item-sortOrder"
                min={1}
                value={sortOrder}
                onChange={(e) => setSortOrder(parseInt(e.target.value) || 1)}
              />
            </div>

            <div>
              {imagePreview && (
                <div className="mb-2">
                  <img
                    src={imagePreview}
                    alt="Aperçu"
                    className="w-16 h-16 object-cover rounded"
                  />
                </div>
              )}
              <Label label="Image (optionnel)" htmlFor="item-image" />
              <input
                type="file"
                id="item-image"
                className="block py-4 px-3 w-full border bg-white rounded-sm mt-1"
                onChange={handleImageChange}
                accept="image/*"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="submit" isLoading={isLoading}>
              {isEditing ? "Mettre à jour" : "Ajouter"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ComponentItemFormModal;
